'use client';

import { msg } from '@/services/isomorphic/I18NService';
import { startTransition, useState, type FC } from 'react';
import { useSearchBox } from '@/react/hooks/useSearchBox';
import type { ISearchSuggestions } from '@/services/models/SearchSuggestions';
import { Icon, IconTypes, IconSizeProp } from '../../core-ui/Icon';
import { Button } from '../../core-ui/Button';
import { SearchModal } from './internal/SearchModal';

import S from './styles.base.module.scss';
import { search_box_search } from "@/lang/__generated__/ahnu/search_box_search";

export interface IMobileSearchButtonProps {
  /**
   * Initial search suggestions to be displayed in the search popover.
   */
  initialSuggestions?: Promise<ISearchSuggestions | undefined>;
}

/**
 * A search button that will trigger a search modal.
 *
 * Intended first to be used on mobile experiences.
 */
const MobileSearchButton: FC<IMobileSearchButtonProps> = ({
  initialSuggestions
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [
    vm,
    {
      navigate,
      reset,
      search,
      // We can use this to present a more meaningful feedback to the user while the search page is loading
      // right now, none is defined, so we are only disabling the button
      // while the search page is loading.
      isNavigatingToResults
    }
  ] = useSearchBox(initialSuggestions);

  const handleCloseModal = (): void => {
    setIsOpen(false);
    reset();
  };

  const handleOpenModal = (): void => {
    // We need to wrap the state update in a transition to prevent the modal from flashing upon
    // `use` promises resolving.
    startTransition(() => setIsOpen(true));
  };

  const handleOnNavigate = (): void => {
    // If the search term is empty, we don't want to navigate to the results page.
    if (vm.term.length === 0) {
      return;
    }

    navigate();
    handleCloseModal();
  };

  const handleOnSearch = async (searchTerm: string): Promise<void> => {
    search(searchTerm);
  };

  return (
    <>
      <SearchModal
        vm={vm}
        onSearch={handleOnSearch}
        onNavigate={handleOnNavigate}
        onClear={reset}
        isOpen={isOpen}
        onClose={handleCloseModal}
      />
      <Button
        variant="text"
        className={S.searchModalButton}
        disabled={isNavigatingToResults}
        onClick={handleOpenModal}
        aria-label={msg(search_box_search)}
      >
        <Icon
          className={S.searchIcon}
          icon={IconTypes.Search}
          size={IconSizeProp.SizeLG}
        />
      </Button>
    </>
  );
};

export default MobileSearchButton;
